<!-- Chapter Settings -->
<app-section-card title="Chapter Settings" [collapsed]="false">
  <div class="px-3 py-3 flex flex-col gap-3">
    <div class="form-row">
      <div class="form-field">
        <label class="form-label">Slug</label>
        <input pInputText class="w-full font-mono" [value]="data().slug"
          (change)="onSlugChange($any($event.target).value)" />
      </div>
      <div class="form-field">
        <label class="form-label">Name</label>
        <input pInputText class="w-full" [value]="data().name ?? ''"
          (input)="patch(d => d.name = $any($event.target).value || undefined)" />
      </div>
    </div>

    <div class="flex flex-wrap gap-x-5 gap-y-1.5 px-0.5">
      <div class="form-field form-field--check">
        <input id="chapter-disabled-check" type="checkbox" [checked]="data().disabled ?? false"
          (change)="patch(d => d.disabled = $any($event.target).checked || undefined)" />
        <label class="form-label" for="chapter-disabled-check">Disabled</label>
      </div>
      <div class="form-field form-field--check">
        <input id="chapter-disable-pause-check" type="checkbox" [checked]="data().disablePause ?? false"
          (change)="patch(d => d.disablePause = $any($event.target).checked || undefined)" />
        <label class="form-label" for="chapter-disable-pause-check">Disable pause</label>
      </div>
      <div class="form-field form-field--check">
        <input id="chapter-disable-replay-check" type="checkbox" [checked]="data().disableReplay ?? false"
          (change)="patch(d => d.disableReplay = $any($event.target).checked || undefined)" />
        <label class="form-label" for="chapter-disable-replay-check">Disable replay</label>
      </div>
      <div class="form-field form-field--check">
        <input id="chapter-disable-osd-check" type="checkbox" [checked]="data().disabledOsdTimeline ?? false"
          (change)="patch(d => d.disabledOsdTimeline = $any($event.target).checked || undefined)" />
        <label class="form-label" for="chapter-disable-osd-check">Disable OSD timeline</label>
      </div>
      <div class="form-field form-field--check">
        <input id="chapter-nonstandard-dims-check" type="checkbox" [checked]="data().nonstandartDimensions ?? false"
          (change)="patch(d => d.nonstandartDimensions = $any($event.target).checked || undefined)" />
        <label class="form-label" for="chapter-nonstandard-dims-check">Non-standard dimensions</label>
      </div>
    </div>

    <!-- DEV-833: gate the WHOLE chapter on an argument. The player reads
         `group`/`groups` + `attributes` off the chapter ref itself
         (`getAvailableChapter`) and skips a chapter that does not match, so
         this replaces repeating the same condition on every scene. -->
    <div>
      <label class="form-label">Attributes</label>
      <app-attributes-editor
        [data]="chapterAttributes()"
        (dataChange)="setChapterAttributes($event)"
      />
      <small class="text-[0.7rem] text-wb-muted leading-snug block mt-1">
        Plays only when every argument here matches. An argument with no value set skips the chapter.
      </small>
    </div>
  </div>
</app-section-card>

<app-dimensions-editor
  title="Dimensions Map"
  [dimensionsMap]="data().dimensionsMap ?? {}"
  (dimensionsMapChange)="patch(d => d.dimensionsMap = $event)"
/>


<!-- Scenes -->
<app-section-card title="Scenes" [badge]="data().scenes.length">
  @if (data().scenes.length === 0) {
    <div class="px-3 py-6 text-center text-wb-muted text-[12px]">No scenes defined.</div>
  } @else {
    <div cdkDropList (cdkDropListDropped)="dropScene($event)" class="item-drop-list p-1.5">
      @for (scene of data().scenes; track $index; let si = $index) {
        <div cdkDrag class="scene-drag-item">
          <div *cdkDragPlaceholder class="drag-placeholder"></div>

          @if ((scene.split ?? 1) > 1) {
            <!-- Split group -->
            <div class="item-row scene-split-header">
              <span cdkDragHandle class="drag-handle"><i class="pi pi-bars text-[9px]"></i></span>
              <i class="pi pi-play text-[10px] text-wb-success shrink-0"></i>
              <span class="font-mono text-[11px] text-wb-text truncate flex-1">{{ scene.slug }}</span>
              @if (scene.externalChapter) {
                <span class="text-[10px] text-wb-muted italic shrink-0">ext</span>
              }
              @if (scene.disabled) {
                <span class="text-[10px] text-wb-danger shrink-0">off</span>
              }
              <span class="text-[10px] text-wb-muted shrink-0">&times;{{ scene.split }}</span>
              @if (!scene.externalChapter) {
                <button pButton [text]="true" size="small" severity="secondary" class="wb-icon-btn"
                  (click)="requestCopyScene(si, $event)" title="Copy scene"><i class="pi pi-copy"></i></button>
              }
              <button pButton [text]="true" size="small" severity="danger" class="wb-icon-btn"
                (click)="removeScene(si, $event)"><i class="pi pi-trash"></i></button>
            </div>
            @for (splitSlug of splitSlugs(scene); track splitSlug) {
              <div class="item-row item-row--link item-row--indented" (click)="onSplitSceneClick(scene, splitSlug, $event)">
                <span class="text-wb-muted text-[10px] shrink-0">&#8627;</span>
                <i class="pi pi-play text-[9px] text-wb-success shrink-0"></i>
                <span class="font-mono text-[11px] text-wb-text truncate flex-1">{{ splitSlug }}</span>
                <i class="pi pi-arrow-right text-[9px] text-wb-muted shrink-0 go-arrow"></i>
              </div>
            }
          } @else {
            <!-- Single scene -->
            <div class="item-row item-row--link" (click)="onSceneClick(scene, $event)">
              <span cdkDragHandle class="drag-handle" (click)="$event.stopPropagation()"><i class="pi pi-bars text-[9px]"></i></span>
              <i class="pi pi-play text-[10px] text-wb-success shrink-0"></i>
              <span class="font-mono text-[11px] text-wb-text truncate flex-1">{{ scene.slug }}</span>
              @if (scene.externalChapter) {
                <span class="text-[10px] text-wb-muted italic shrink-0">ext</span>
              }
              @if ($any(scene).variant) {
                <span class="text-[10px] text-wb-accent shrink-0" title="Variant"><i class="pi pi-users text-[9px]"></i></span>
              }
              @if ($any(scene).groups?.length) {
                <span class="text-[10px] text-wb-accent-2 shrink-0" title="Has attributes"><i class="pi pi-tag text-[9px]"></i></span>
              }
              @if (scene.disabled) {
                <span class="text-[10px] text-wb-danger shrink-0">off</span>
              }
              @if ($any(scene).alt) {
                <span class="text-[10px] text-wb-muted shrink-0" title="Alternative (fallback) scene">alt</span>
              }
              <button pButton [text]="true" size="small" severity="secondary" class="wb-icon-btn"
                (click)="toggleSceneExpand(si, $event)" title="Edit attributes"><i [class]="expandedScene() === si ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"></i></button>
              @if (!scene.externalChapter) {
                <button pButton [text]="true" size="small" severity="secondary" class="wb-icon-btn"
                  (click)="requestCopyScene(si, $event)" title="Copy scene"><i class="pi pi-copy"></i></button>
              }
              <button pButton [text]="true" size="small" severity="danger" class="wb-icon-btn"
                (click)="removeScene(si, $event)"><i class="pi pi-trash"></i></button>
              <i class="pi pi-arrow-right text-[9px] text-wb-muted shrink-0 go-arrow"></i>
            </div>
            @if (expandedScene() === si) {
              <div class="px-3 py-2 bg-wb-bg flex flex-col gap-2">
                <div class="flex items-center gap-1.5">
                  <p-checkbox [inputId]="'scene-alt-check-' + si" [binary]="true" [ngModel]="$any(scene).alt ?? false" (ngModelChange)="setSceneAlt(si, $event)" />
                  <label class="form-label" [attr.for]="'scene-alt-check-' + si">Alternative scene</label>
                </div>
                <div>
                  <label class="form-label">Attributes</label>
                  <app-attributes-editor
                    [data]="$any(scene).attributes ?? null"
                    (dataChange)="setSceneAttributes(si, $event)"
                  />
                </div>
                @if ($any(scene).variant) {
                  <app-variant-editor
                    [data]="$any(scene).variant"
                    mode="scene"
                    [argKeys]="argKeys()"
                    [siblingSlug]="getSceneSlugs()"
                    (dataChange)="setSceneVariant(si, $event)"
                    (namesMapPopulate)="namesMapPopulate.emit($event)"
                  />
                }
              </div>
            }
          }
        </div>
      }
    </div>
  }
  <div class="px-3 py-2 flex gap-2">
    <button class="add-item-btn" type="button" (click)="openSceneVideoPicker()"><i class="pi pi-video text-[9px]"></i> Add Scenes from Videos</button>
    <button class="add-item-btn" type="button" (click)="openSceneImagePicker()"><i class="pi pi-image text-[9px]"></i> Add Scenes from Images</button>
  </div>
</app-section-card>

<app-media-picker
  [(visible)]="sceneVideoPickerVisible"
  [categories]="sceneVideoPickerCategories"
  [multi]="true"
  currentValue=""
  (filesSelected)="onSceneVideosSelected($event)"
/>

<app-media-picker
  [(visible)]="sceneImagePickerVisible"
  [categories]="sceneImagePickerCategories"
  [multi]="true"
  currentValue=""
  (filesSelected)="onSceneImagesSelected($event)"
/>

<!-- Images -->
<app-section-card title="Images" [badge]="(data().images ?? []).length">
  @if ((data().images ?? []).length === 0) {
    <div class="px-3 py-4 text-center text-wb-muted text-[12px]">No images defined.</div>
  } @else {
    <div cdkDropList (cdkDropListDropped)="dropImage($event)" class="item-drop-list p-1.5">
      @for (img of data().images; track $index; let ii = $index) {
        <div cdkDrag>
          <div class="item-row item-row--link" (click)="onImageClick(img, $event)">
            <div *cdkDragPlaceholder class="drag-placeholder"></div>
            <span cdkDragHandle class="drag-handle" (click)="$event.stopPropagation()"><i class="pi pi-bars text-[9px]"></i></span>
            <i class="pi pi-image text-[10px] text-wb-accent-2 shrink-0"></i>
            <span class="font-mono text-[11px] text-wb-text truncate flex-1">{{ img.slug }}</span>
            @if ($any(img).groups?.length) {
              <span class="text-[10px] text-wb-accent-2 shrink-0" title="Has attributes"><i class="pi pi-tag text-[9px]"></i></span>
            }
            @if (img.thumbnail) {
              <span class="text-[10px] text-wb-warn shrink-0">thumb</span>
            }
            @if (img.disabled) {
              <span class="text-[10px] text-wb-danger shrink-0">off</span>
            }
            <button pButton [text]="true" size="small" severity="secondary" class="wb-icon-btn"
              (click)="toggleImageExpand(ii, $event)" title="Edit attributes"><i [class]="expandedImage() === ii ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"></i></button>
            <button pButton [text]="true" size="small" severity="secondary" class="wb-icon-btn"
              (click)="requestCopyImage(ii, $event)" title="Copy image"><i class="pi pi-copy"></i></button>
            <button pButton [text]="true" size="small" severity="danger" class="wb-icon-btn"
              (click)="removeImage(ii, $event)"><i class="pi pi-trash"></i></button>
            <i class="pi pi-arrow-right text-[9px] text-wb-muted shrink-0 go-arrow"></i>
          </div>
          @if (expandedImage() === ii) {
            <div class="px-3 py-2 bg-wb-bg">
              <label class="form-label">Attributes</label>
              <app-attributes-editor
                [data]="$any(img).attributes ?? null"
                (dataChange)="setImageAttributes(ii, $event)"
              />
            </div>
          }
        </div>
      }
    </div>
  }
  <div class="px-3 py-2">
    <button class="add-item-btn" type="button" (click)="openImagePicker()"><i class="pi pi-image text-[9px]"></i> Add Images from Files</button>
  </div>
</app-section-card>

<app-media-picker
  [(visible)]="imagePickerVisible"
  [categories]="imagePickerCategories"
  [multi]="true"
  currentValue=""
  (filesSelected)="onImagesSelected($event)"
/>

<!-- Audio Tracks -->
<app-audio-track-editor
  [audioTrack]="data().audioTrack ?? []"
  [argKeys]="argKeys()"
  [sceneSlugs]="sceneSlugs()"
  (audioTrackChange)="onAudioTrackChange($event)"
  (namesMapPopulate)="namesMapPopulate.emit($event)"
/>

